import { useContext, useState } from "react";
import { EmailTemplateContext } from "../../../context/EmailTemplateContext";
import { ScreenSizeContext } from "../../../context/ScreenSizeContext";
import ButtonComponent from "../Element/ButtonComponent";
import TextComponent from "../Element/TextComponent";
import ImageComponent from "../Element/ImageComponent";
import LogoComponent from "../Element/LogoComponent";
import DividerComponent from "../Element/DividerComponent";
import { SelectedElementContex } from "../../../context/SelectedElement";
import { ArrowDown, ArrowUp, Trash } from "lucide-react";
import UrlComponent from "../Element/UrlComponent";
import SocialHandles from "../Element/SocialHandles";
import TableComponent from "../Element/TableComponent";

export default function ColumnLayout({ layout }) {
  const [dragOver, setDragOver] = useState(null);
  const { emailTemplate, setEmailTemplate } = useContext(EmailTemplateContext);
  const { dragElementLayout, setDragElementLayout } =
    useContext(ScreenSizeContext);
  const { selectedElement, setSelectedElement } = useContext(
    SelectedElementContex
  );

  const onDragOverHandle = (event, index) => {
    event.preventDefault();
    setDragOver({
      index: index,
      columnId: layout?.id,
    });
  };

  const onDragLeaveHandle = (event, index) => {
    event.preventDefault();
    setDragOver(null);
  };

  const onDropHandle = () => {
    const index = dragOver.index;
    setEmailTemplate((prevItem) =>
      prevItem?.map((col) =>
        col.id === layout?.id
          ? { ...col, [index]: dragElementLayout?.dragElement }
          : col
      )
    );
    setDragOver(null);
  };

  const deleteElementLayout = (id) => {
    const updatedEmailTemplate = emailTemplate?.filter((item) => item.id != id);
    setEmailTemplate(updatedEmailTemplate);
    setSelectedElement(null);
  };

  const moveLayoutUp = (id) => {
    const index = emailTemplate.findIndex((item) => item.id === id);
    if (index > 0) {
      setEmailTemplate((prevItems) => {
        const updatedItems = [...prevItems];
        // swap current item with the one above it
        [updatedItems[index], updatedItems[index - 1]] = [
          updatedItems[index - 1],
          updatedItems[index],
        ];
        return updatedItems;
      });
    }
  };
  const moveLayoutDown = (id) => {
    const index = emailTemplate.findIndex((item) => item.id === id);
    if (index < emailTemplate.length - 1) {
      setEmailTemplate((prevItems) => {
        const updatedItems = [...prevItems];
        // swap current item with the one above it
        [updatedItems[index], updatedItems[index + 1]] = [
          updatedItems[index + 1],
          updatedItems[index],
        ];
        return updatedItems;
      });
    }
  };

  const GetElementComponent = (element) => {
    // console.log(element);
    if (element?.type === "Button") {
      return <ButtonComponent {...element} />;
    } else if (element?.type === "Text") {
      return <TextComponent {...element} />;
    } else if (element?.type === "Image") {
      return <ImageComponent {...element} />;
    } else if (element?.type === "Logo") {
      return <LogoComponent {...element} />;
    } else if (element?.type === "Divider") {
      return <DividerComponent {...element} />;
    } else if (element?.type === "Url") {
      return <UrlComponent {...element} />;
    } else if (element?.type === "SocialHandles") {
      return <SocialHandles {...element} />;
    } else if (element?.type === "Table") {
      return (
        <TableComponent
          tableData={element}
          onUpdateTable={(updated) => {
            const newSel = { ...selectedElement };
            newSel.layout[selectedElement.index] = updated;
            setSelectedElement(newSel);
          }}
        />
      );
    }
    return element?.type;
  };

  return (
    <div style={{ position: "relative" }}>
      {/* MAIN LAYOUT TABLE */}
      <table
        role="presentation"
        width="100%"
        cellSpacing="0"
        cellPadding="0"
        style={{
          width: "100%",
          border:
            selectedElement?.layout?.id === layout?.id
              ? "1px dashed #3B82F6"
              : "none",
        }}
      >
        <tr>
          {Array.from({ length: layout?.numOfCol }).map((_, index) => {
            const isSelected =
              selectedElement?.layout?.id === layout?.id &&
              selectedElement?.index === index;

            const isDragOver = dragOver?.index === index && dragOver?.columnId;

            return (
              <td
                key={index}
                align="left"
                valign="middle"
                style={{
                  width: `${100 / layout?.numOfCol}%`,
                  padding: "0px",
                  backgroundColor: isDragOver ? "#86EFAC" : "#FFFFFF",
                  border: isSelected ? "2px solid #3B82F6" : "none",
                  cursor: "pointer",
                  verticalAlign: "middle",
                }}
                onDragOver={(event) => onDragOverHandle(event, index)}
                onDragLeave={onDragLeaveHandle}
                onDrop={onDropHandle}
                onClickCapture={() =>
                  setSelectedElement({ layout: layout, index: index })
                }
              >
                {GetElementComponent(layout?.[index]) || (
                  <div
                    style={{
                      padding: "10px",
                      textAlign: "center",
                      color: "#666",
                    }}
                  >
                    Drag Element Here
                  </div>
                )}
              </td>
            );
          })}
        </tr>
      </table>

      {/* BUILDER CONTROLS (Delete / Up / Down) */}
      {selectedElement?.layout?.id === layout?.id && (
        <div
          style={{
            position: "absolute",
            right: "-40px",
            top: "0",
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          {/* DELETE */}
          <div
            onClick={() => deleteElementLayout(layout?.id)}
            style={{
              cursor: "pointer",
              backgroundColor: "#F3F4F6",
              padding: "8px",
              borderRadius: "999px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Trash style={{ width: "16px", height: "16px", color: "red" }} />
          </div>

          {/* MOVE UP */}
          <div
            onClick={() => moveLayoutUp(layout?.id)}
            style={{
              cursor: "pointer",
              backgroundColor: "#F3F4F6",
              padding: "8px",
              borderRadius: "999px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <ArrowUp style={{ width: "16px", height: "16px" }} />
          </div>

          {/* MOVE DOWN */}
          <div
            onClick={() => moveLayoutDown(layout?.id)}
            style={{
              cursor: "pointer",
              backgroundColor: "#F3F4F6",
              padding: "8px",
              borderRadius: "999px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <ArrowDown style={{ width: "16px", height: "16px" }} />
          </div>
        </div>
      )}
    </div>
  );
}
